import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { sleep } from "@/utils/sleep";
import { trackEvent, trackException } from "@/appInsights";
import { CLOSE_REASON } from "@/constants";

export const RECONNECT_RETRY_DELAYS_MS = Object.freeze([0, 2000, 5000, 10000, 15000, 30000, 30000, 30000]);

export const STOP_MAX_ATTEMPTS = 5;

export const STOP_RETRY_DELAY_MS = 1000;

let connection;

let isServerRequested = false;

let requestedStopReason;

/* The hub's own close is the only one this file can name by itself; every other
   deliberate close is whatever the stop() caller said it was, since the Stop
   button and the idle timer arrive here as the same call. What is left is a
   close nobody asked for, so CONNECTION_LOST is the remainder rather than a
   diagnosis. */
function getCloseReason() {
  if (isServerRequested) {
    return CLOSE_REASON.SERVER_REQUESTED;
  }
  if (requestedStopReason) {
    return requestedStopReason;
  }

  return CLOSE_REASON.CONNECTION_LOST;
}

function createConnection({ accessTokenFactory, onClose, onIdentificationRefreshed, onReconnecting, onReconnected }) {
  const newConnection = new HubConnectionBuilder()
    .withUrl(`${process.env.VUE_APP_ARBITRIUM_API}/identifications`, {
      accessTokenFactory
    })
    .withAutomaticReconnect(RECONNECT_RETRY_DELAYS_MS)
    .configureLogging(process.env.NODE_ENV === "development" ? LogLevel.Information : LogLevel.Warning)
    .build();
  newConnection.on("identificationRefreshed", data => {
    trackEvent({
      name: "signalR/on/identificationRefreshed",
      properties: { data }
    });
    onIdentificationRefreshed(data);
  });
  newConnection.on("pingRequest", () => {
    trackEvent({ name: "signalR/on/pingRequest" });
    newConnection.invoke("PingResponse").catch(exception => {
      trackException({
        exception,
        properties: { source: "signalR/invoke/PingResponse" }
      });
    });
  });
  newConnection.on("disconnectConnection", () => {
    trackEvent({ name: "signalR/on/disconnectConnection" });
    isServerRequested = true;
    newConnection.stop().catch(exception => {
      isServerRequested = false;
      trackException({
        exception,
        properties: { source: "signalR/on/disconnectConnection" }
      });
    });
  });
  newConnection.onclose(error => {
    const reason = getCloseReason();

    isServerRequested = false;
    requestedStopReason = undefined;
    trackEvent({
      name: "signalR/onclose",
      properties: { reason, error: error?.message }
    });
    onClose({ reason, error });
  });
  newConnection.onreconnecting(error => {
    trackEvent({
      name: "signalR/onreconnecting",
      properties: { error: error?.message }
    });
    onReconnecting();
  });
  newConnection.onreconnected(() => {
    trackEvent({ name: "signalR/onreconnected" });
    onReconnected();
  });

  return newConnection;
}

function getConnection(options) {
  if (!connection) {
    connection = createConnection(options);
  }
  return connection;
}

export async function start(options) {
  const started = getConnection(options);

  isServerRequested = false;
  requestedStopReason = undefined;

  await started.start();
}

/* The default is deliberately vague: a caller that names no reason should read
   as unaccounted for, never as the operator having pressed Stop. */
export async function stop(reason = CLOSE_REASON.CLIENT_STOPPED) {
  if (!connection) return;

  requestedStopReason = reason;
  try {
    let tryCount = 0;
    while (connection.state !== HubConnectionState.Disconnected && tryCount < STOP_MAX_ATTEMPTS) {
      if (tryCount > 0) await sleep(STOP_RETRY_DELAY_MS);
      await connection.stop();
      tryCount++;
    }
    if (connection.state !== HubConnectionState.Disconnected) {
      throw new Error(`Work status is stuck in "${connection.state}" state.`);
    }
  } finally {
    requestedStopReason = undefined;
  }
}

export async function invoke(...args) {
  return await connection.invoke(...args);
}
